import React, { useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Body1, Body2 } from "@activecollab/components";
import PropTypes from "prop-types";
import NotFoundSlate from "~/components/EmptySlate/NotFoundSlate";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener";
import { Page } from "~/components/Page";
import { Resolver } from "~/components/Resolver.jsx";
import { showErrorToast } from "~/components/Toast";
import { TrashSlate } from "~/components/TrashSlate/TrashSlate";
import {
  fetchCalendarEvent,
  moveCalendarEventsToTrashFromEventStreamByCalendarId,
  restoredFromTrash,
  setCalendarEventFromEventStream,
} from "~/store/actions/actions.calendarEvents";
import { fetchCalendar, fetchCalendars, setCalendarFromEventStream } from "~/store/actions/actions.calendars";
import { fetchObjectLastVisitTimestamp } from "~/store/actions/actions.lastVisit";
import { permanentlyDelete } from "~/store/actions/actions.trash";
import { getCalendarEventById } from "~/store/selectors/selector.calendarEvents";
import { getCalendarById } from "~/store/selectors/selector.calendars";
import { useSecurity } from "../Hooks";
import { useObjectSheetContext } from "../Sheet/ObjectSheetContext";

const NotFound = () => {
  const { t } = useTranslation();
  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-mb-8">
      <NotFoundSlate
        header={t("This event is not found")}
        pageName={t("calendar page")}
        pageLink="/calendar"
      />
    </div>
  );
};

export const CalendarEventResolver = ({ id, calendarId, children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const can = useSecurity();
  const queryClient = useQueryClient();
  const { open, closeSheet } = useObjectSheetContext();
  // const [notFoundShow, setNotFoundShow] = useState(false);
  const calendar = useSelector((state) => getCalendarById(state, calendarId));
  const event = useSelector((state) => getCalendarEventById(state, id));

  const isCalendarTrashed = calendar ? calendar.is_trashed : false;

  const resolveDependencies = useCallback(async () => {
    await dispatch(fetchObjectLastVisitTimestamp("CalendarEvent", id));
    await dispatch(fetchCalendar(calendarId)).then((action) => {
      if (action.error) {
        throw action.error;
      }
    });
    await dispatch(fetchCalendars()).then((action) => {
      if (action.error) {
        throw action.error;
      }
    });
    await dispatch(fetchCalendarEvent(calendarId, id)).then((action) => {
      if (action.error) {
        throw action.error;
      }
    });
  }, [calendarId, dispatch, id]);

  const canRestore = can(["restore"], event);
  const canPermanentlyDelete = can(["permanently-delete"], event);

  const type = "Event";

  const handleRestore = useCallback(async () => {
    const { error } = await dispatch(restoredFromTrash(id));
    if (error) {
      showErrorToast(t("Unable to restore event"));
    } else {
      queryClient.invalidateQueries(["calendar-event", calendarId, id]);
    }
  }, [calendarId, dispatch, id, queryClient, t]);

  const handleDelete = useCallback(async () => {
    await dispatch(permanentlyDelete("CalendarEvent", id));
    queryClient.removeQueries(["calendar-event", calendarId, id]);
    closeSheet();
  }, [calendarId, closeSheet, dispatch, id, queryClient]);

  useGlobalPrivateChannelListener("CalendarEventUpdated", (data) => {
    dispatch(setCalendarEventFromEventStream(data));
    if (data.calendar_id !== calendarId && data.id === id) {
      if (open) {
        navigate(`?modal=CalendarEvent-${data.id}-${data.calendar_id}`);
      } else {
        navigate(`/calendar/${data.calendar_id}/events/${data.id}`);
      }
    }
  });

  useGlobalPrivateChannelListener("CalendarEventMovedToTrash", (data) => {
    if (data.id === id) {
      dispatch(setCalendarEventFromEventStream(data));
    }
  });

  useGlobalPrivateChannelListener("CalendarEventRestoredFromTrash", (data) => {
    if (data.id === id) {
      dispatch(setCalendarEventFromEventStream(data));
    }
  });

  useGlobalPrivateChannelListener("UserCalendarMovedToTrash", (data) => {
    if (data.id === calendarId) {
      dispatch(setCalendarFromEventStream(data));
      dispatch(moveCalendarEventsToTrashFromEventStreamByCalendarId(data.id));
    }
  });

  useGlobalPrivateChannelListener("UserCalendarRestoredFromTrash", (data) => {
    if (data.id === calendarId) {
      dispatch(setCalendarFromEventStream(data));
    }
  });

  useGlobalPrivateChannelListener("DataObjectDeleted", (data) => {
    if (data?.type === "CalendarEvent" && data.id === id) {
      if (open) {
        closeSheet();
      } else {
        navigate("/calendar");
      }
    }
  });

  // if (notFoundShow) {
  //   return <NotFound />;
  // }

  return (
    <Resolver
      loadingComponent={<Page.Loader />}
      callback={resolveDependencies}
      errorComponent={<NotFound />}
      key={`CalendarEvent-${id}-${calendarId}`}
    >
      <TrashSlate
        type={type}
        className="tw-h-full tw-items-center"
        subtitle={
          <>
            <Body1
              align="center"
              color="primary"
              className="tw-text-center tw-text-theme-900"
            >
              <Trans>This {type.toLowerCase()} is deleted</Trans>.
            </Body1>

            {canRestore && !isCalendarTrashed ? (
              <Body2
                color="primary"
                align="center"
                className="tw-mt-2"
              >
                {t("If you want to work on it again, recover it")}.
              </Body2>
            ) : null}
          </>
        }
        isTrashed={event?.is_trashed}
        trashedOn={event?.trashed_on}
        trashedById={event?.trashed_by_id}
        createdById={event?.created_by_id}
        createdByName={event?.created_by_name}
        createdOn={event?.created_on}
        canRestore={canRestore}
        canPermanentlyDelete={canPermanentlyDelete}
        onRestore={handleRestore}
        onPermanentlyDelete={handleDelete}
      >
        {children}
      </TrashSlate>
    </Resolver>
  );
};

CalendarEventResolver.propTypes = {
  id: PropTypes.number.isRequired,
  calendarId: PropTypes.number.isRequired,
  children: PropTypes.element.isRequired,
};
